/**
 * 全局校区范围上下文（O-21 顶栏全局校区切换器）。
 *
 * 全局校区范围（'ALL' 或校区 ID）是顶栏切换器唯一可修改的客户端界面偏好，
 * 各业务页面数据请求的 campusId 参数统一取自本上下文（锁定模式：页内无校区筛选、无临时态）。
 *
 * 防越权时序（admin-global-campus-switch:SC-05/SC-06/SC-07）：范围值初始恒为 'ALL'；
 * 持久化值仅在权限快照就绪（非 loading 且非 null）且校区列表就绪后才校验应用——
 * 总部（hasAllOrgScope===true）要求值 ∈ ACTIVE 校区集合，非总部要求值 ∈ grantedCampusIds，
 * 不通过静默丢弃保持 'ALL'。
 *
 * 就绪门控（scopeReady，修复刷新闪错误口径）：范围解析完成（持久化值已校验应用，
 * 或权限快照拉取失败明确回落 'ALL'）前 scopeReady=false，AdminLayout 据此不挂载页面
 * 内容——页面首查即以最终口径发出，杜绝「先按 ALL 查询并渲染错误口径、就绪后再纠正」
 * 的首屏闪烁与废查询；手改越权 ID 依旧不产生未授权请求。
 * 权限快照就绪前 /campuses 亦不拉取（过期会话启动仅发一次鉴权探测，SC-07 同源时序）。
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useSession } from './SessionContext';
import {
  loadPersistedGlobalCampus,
  persistGlobalCampus,
  type GlobalCampusScope,
} from './global-campus-storage';
import { useCampusOptions } from '../hooks/useCampusOptions';
import type { CampusItem } from '../api/campuses.api';

interface GlobalCampusContextValue {
  /** 当前全局校区范围：'ALL' 或校区 ID */
  scope: GlobalCampusScope;
  /** 范围解析是否完成（持久化值已校验应用，或权限快照拉取失败明确回落 'ALL'）；完成前页面数据请求不得发起 */
  scopeReady: boolean;
  /** 更新全局校区范围（顶栏切换器调用；同步持久化，刷新后经校验恢复） */
  setScope: (scope: GlobalCampusScope) => void;
  /** ACTIVE 校区列表（供切换器选项渲染，与本 Provider 校验共用一次加载） */
  campuses: CampusItem[];
  /** 校区列表加载中 */
  campusesLoading: boolean;
}

const GlobalCampusContext = createContext<GlobalCampusContextValue | null>(null);

export function GlobalCampusScopeProvider({ children }: { children: ReactNode }) {
  const [scope, setScopeState] = useState<GlobalCampusScope>('ALL');
  // 范围解析完成标志：就绪前 AdminLayout 以加载态代替页面挂载（页面首查即最终口径）。
  // 失败放行后若权限快照经刷新到达，持久化值仍会校验应用并触发页面按新口径重查（沿用旧行为）。
  const [scopeReady, setScopeReady] = useState(false);
  // 持久化值一次性应用守卫：仅首次权限+校区列表双就绪时应用，此后由用户经切换器显式修改
  const appliedRef = useRef(false);
  // 是否真实经历过权限快照拉取（loading=true→false 往返）：SessionProvider 的启动拉取由
  // 兄弟组件 PermissionRefreshOnBoot 的 effect 触发，本组件首轮 effect 读到的 loading 初始值
  // 尚未翻真——「null 且未经历 loading」是拉取未开始而非失败，不得据此放行页面
  const sawLoadingRef = useRef(false);
  const { permissions, permissionsLoading } = useSession();
  // 权限快照就绪信号：加载中未定；加载完有快照=成功；加载完无快照且经历过拉取=失败放行。
  // /campuses 等就绪后才拉取：过期会话恢复路径只剩 /auth/permissions 一个探测请求
  //（401 时布局随登出整体卸载，失败放行分支与校区拉取天然不再执行；非 401 失败维持 fail-open）
  const permissionsSettled =
    permissionsLoading ? false : permissions !== null || sawLoadingRef.current;
  const { campuses, loading: campusesLoading } = useCampusOptions(permissionsSettled);

  useEffect(() => {
    // 权限快照加载中：继续等待（不以未校验值放行页面查询，也不应用持久化值）
    if (permissionsLoading) {
      sawLoadingRef.current = true;
      return;
    }
    // 权限快照拉取失败（真实经历拉取后仍为空）：无法校验持久化值，放行页面回落 'ALL'
    //（与既有失败口径一致，不永久阻塞）；拉取尚未开始（首帧）则继续等待
    if (!permissions) {
      if (!sawLoadingRef.current) return;
      setScopeReady(true);
      return;
    }
    // 校区列表未就绪：总部校验依赖 ACTIVE 集合
    if (campusesLoading) return;
    if (!appliedRef.current) {
      appliedRef.current = true;
      const persisted = loadPersistedGlobalCampus();
      if (!persisted || persisted === 'ALL') {
        // 无持久化值（或显式 ALL）：无需应用，直接就绪
      } else if (permissions.hasAllOrgScope === true) {
        // 总部：必须命中 ACTIVE 校区集合（不存在的 ID/停用校区一律丢弃）
        if (campuses.some((c) => c.id === persisted)) {
          setScopeState(persisted);
        }
      } else if (permissions.grantedCampusIds?.includes(persisted)) {
        // 非总部：仅授权校区集合内的值可应用
        setScopeState(persisted);
      }
      // 校验不通过：静默丢弃保持 'ALL'（不以越权校区为条件发起任何请求）
    }
    setScopeReady(true);
  }, [permissions, permissionsLoading, campuses, campusesLoading]);

  const setScope = useCallback((next: GlobalCampusScope) => {
    setScopeState(next);
    persistGlobalCampus(next);
  }, []);

  return (
    <GlobalCampusContext.Provider
      value={{ scope, scopeReady, setScope, campuses, campusesLoading }}
    >
      {children}
    </GlobalCampusContext.Provider>
  );
}

export function useGlobalCampusScope(): GlobalCampusContextValue {
  const ctx = useContext(GlobalCampusContext);
  if (!ctx) throw new Error('useGlobalCampusScope 必须在 GlobalCampusScopeProvider 内使用');
  return ctx;
}
